import type { Row, TrendWindow } from './types'

export interface DailyTrend {
  date: string
  opened: number
  resolved: number
  cum: number
}

export interface TrendBucket {
  label: string
  opened: number
  resolved: number
  cum: number
}

function plus(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) || 0) + 1)
}

export function buildDaily(rows: Row[], asOf: string): DailyTrend[] {
  const opened = new Map<string, number>()
  const resolved = new Map<string, number>()
  let first: string | null = null
  rows.forEach((row) => {
    if (row.created) {
      plus(opened, row.created)
      if (!first || row.created < first) first = row.created
    }
    if (row.resolved) plus(resolved, row.resolved)
  })
  if (!first) return []

  const results: DailyTrend[] = []
  let cumulative = 0
  const date = new Date(`${first}T00:00:00Z`)
  while (date.toISOString().slice(0, 10) <= asOf) {
    const day = date.toISOString().slice(0, 10)
    const openedCount = opened.get(day) || 0
    const resolvedCount = resolved.get(day) || 0
    cumulative += openedCount - resolvedCount
    results.push({ date: day, opened: openedCount, resolved: resolvedCount, cum: cumulative })
    date.setUTCDate(date.getUTCDate() + 1)
  }
  return results
}

export function sliceWindow(daily: DailyTrend[], window: TrendWindow, asOf: string): DailyTrend[] {
  if (!daily.length || window === 'all') return daily
  let start: string
  if (window === 'ytd') start = `${asOf.slice(0, 4)}-01-01`
  else {
    const date = new Date(`${asOf}T00:00:00Z`)
    date.setUTCDate(date.getUTCDate() - (Number(window.slice(0, -1)) - 1))
    start = date.toISOString().slice(0, 10)
  }
  return daily.filter((point) => point.date >= start)
}

export function rebucket(daily: DailyTrend[], window: TrendWindow): TrendBucket[] {
  if (!daily.length) return []
  if (window === '30d' || window === '60d' || window === '90d') {
    return daily.map((point) => ({
      label: point.date.slice(5).replace('-', '/'),
      opened: point.opened,
      resolved: point.resolved,
      cum: point.cum,
    }))
  }
  if (window === 'ytd') {
    const buckets: TrendBucket[] = []
    for (let index = 0; index < daily.length; index += 7) {
      const week = daily.slice(index, index + 7)
      buckets.push({
        label: week[0].date.slice(5).replace('-', '/'),
        opened: week.reduce((sum, point) => sum + point.opened, 0),
        resolved: week.reduce((sum, point) => sum + point.resolved, 0),
        cum: week[week.length - 1].cum,
      })
    }
    return buckets
  }

  const months = new Map<string, TrendBucket>()
  daily.forEach((point) => {
    const month = point.date.slice(0, 7)
    const existing = months.get(month) || { label: month.slice(2), opened: 0, resolved: 0, cum: 0 }
    existing.opened += point.opened
    existing.resolved += point.resolved
    existing.cum = point.cum
    months.set(month, existing)
  })
  return Array.from(months.keys()).sort().map((month) => months.get(month) as TrendBucket)
}

export function netInWindow(rows: Row[], window: TrendWindow, asOf: string): { opened: number, resolved: number, net: number } {
  const points = sliceWindow(buildDaily(rows, asOf), window, asOf)
  const opened = points.reduce((sum, point) => sum + point.opened, 0)
  const resolved = points.reduce((sum, point) => sum + point.resolved, 0)
  return { opened, resolved, net: opened - resolved }
}

export function verdictOf(net: number): 'up' | 'down' | 'flat' {
  return net > 0 ? 'up' : net < 0 ? 'down' : 'flat'
}
